import React, { FC, lazy, useMemo } from 'react';
import { RouteObject, useRoutes } from 'react-router-dom';
import { Spin } from 'antd';
import { RouteContext, RouteType } from '@fregata/shared';
import 'assets/less/index.less';
import ErrorBoundary from 'ErrorBoundary';
import AppSearch from 'pages/plat-app-search';
import AppDaily from 'pages/plat-sys-manage/app-daily-activeness';
import UserAction from 'pages/plat-sys-manage/user-action-log';

const SecurityLayout = lazy(() => import('layouts/SecurityLayout'));
const BasicLayout = lazy(() => import('layouts/BasicLayout'));
const ColumnLayout = lazy(() => import('layouts/ColumnLayout'));
const SiderLayout = lazy(() => import('layouts/SiderLayout'));
const GuestLayout = lazy(() => import('layouts/GuestLayout'));

const NotFound = lazy(() => import('pages/NotFound'));
const Login = lazy(() => import('./pages/accounts/Login'));
const SignupSuccess = lazy(() => import('./pages/accounts/SignupSuccess'));
const ForgotStep2 = lazy(() => import('./pages/accounts/ForgotStep2'));
const Forgot = lazy(() => import('./pages/accounts/Forgot'));
const PasswordForgotSuccess = lazy(() => import('./pages/accounts/PasswordForgotSuccess'));
const Signup = lazy(() => import('./pages/accounts/Signup'));

// 普通用户
const CommonIndex = lazy(() => import('pages/common-index'));
const CommonApp = lazy(() => import('pages/common-app'));
const CommonTodos = lazy(() => import('pages/common-todos'));
const CommonMsg = lazy(() => import('pages/common-msg'));

// 平台-企业管理
const EnterpriseList = lazy(() => import('./pages/plat-etp-list'));
const EnterpriseEdit = lazy(() => import('./pages/plat-etp-list/edit'));
const EnterpriseApproval = lazy(() => import('./pages/plat-etp-list/approval'));
// 平台-应用管理
const AppList = lazy(() => import('./pages/plat-app-list'));
const AppEdit = lazy(() => import('./pages/plat-app-list/edit'));
const AppNewData = lazy(() => import('./pages/plat-app-list/newData'));
// 平台-用户管理
const OnlineUser = lazy(() => import('./pages/plat-user-manage/OnlineUser'));
const UserList = lazy(() => import('./pages/plat-user-manage/UserList'));
const DevUserList = lazy(() => import('./pages/plat-user-manage/DevList'));
// 平台-系统管理
const PlatSysAppVer = lazy(() => import('./pages/plat-sys-app-ver'));
const PlatSysExceptionLog = lazy(() => import('pages/plat-sys-manage/exception-log'));
const PlatSysAppLog = lazy(() => import('pages/plat-sys-app-log'));
const PlatSysUserLog = lazy(() => import('pages/plat-sys-manage/plat-sys-user-log'));
// 平台-开发者社区
const PlatDevUserInfo = lazy(() => import('pages/plat-dev-user/user-info'));
const PlatDevOnlineUser = lazy(() => import('pages/plat-dev-user/online-user'));
const PlatDevLoginLog = lazy(() => import('pages/plat-dev-user/login-log'));
const PlatDevMonthlyActiveness = lazy(() => import('pages/plat-dev-user/monthly-activeness'));
const PlatDevProj = lazy(() => import('pages/plat-dev-proj'));
const PlatDevUseCase = lazy(() => import('pages/plat-dev-use-case'));
const PlatDevEtp = lazy(() => import('pages/plat-dev-etp'));

// corp-overview 企业-系统日志概览
const SystemOverview = lazy(() => import('./pages/corp-all-overview'));
// corp-etp 企业-企业配置中心
const CorpEtpOrganization = lazy(() => import('./pages/corp-etp-organization'));
const CorpEtpPlatformAuth = lazy(() => import('./pages/corp-etp-platform-auth'));
const CorpEtpSecNodes = lazy(() => import('./pages/corp-etp-sec-nodes'));
const CorpEtpAuthResult = lazy(() => import('./pages/corp-etp-auth-result'));
// corp-app 企业-应用配置中心
const CorpAppApp = lazy(() => import('./pages/corp-app-app'));
const CorpAppPermission = lazy(() => import('./pages/corp-app-permission'));
// corp-dev 企业-开发配置中心
const CorpDevMember = lazy(() => import('./pages/corp-dev-member'));
const CorpDevCommunicator = lazy(() => import('./pages/corp-dev-communicator'));
const CorpDevCommunicationAuth = lazy(() => import('./pages/corp-dev-communication-auth'));
const CorpDevThirdParty = lazy(() => import('./pages/corp-dev-third-party'));
// corp-sys 企业-系统管理
const FeedbackManagement = lazy(() => import('./pages/feeedback-management'));
const CorpSysAnnouncement = lazy(() => import('./pages/corp-sys-announcement'));
const CorpSysTodo = lazy(() => import('./pages/corp-sys-todo'));
const CorpSysUserLog = lazy(() => import('./pages/corp-sys-user-log'));
const CorpSysAppLog = lazy(() => import('./pages/corp-sys-app-log'));
const Index = lazy(() => import('./pages/Index'));
// corp-mobile 企业-移动端页面配置
const Banner = lazy(() => import('./pages/corp-mobile-banner'));
const DataCard = lazy(() => import('./pages/corp-mobile-dataCard'));
const NewsList = lazy(() => import('./pages/corp-mobile-news'));
const App: FC<any> = () => {
  const routes: RouteType[] = useMemo(
    () => [
      // { title: '权限不足', path: '/', element: <Unauthorized /> },
      // 登录后
      {
        title: '首页',
        path: '/',
        element: <SecurityLayout />, // 基础的布局不需要异步，避免页面抖动
        children: [
          {
            element: <BasicLayout />, // 基础的布局不需要异步，避免页面抖动
            children: [
              // 带菜单
              {
                element: <SiderLayout />,
                children: [
                  { index: true, element: <Index /> },
                  {
                    title: '企业管理',
                    path: 'etpList',
                    element: <EnterpriseList />,
                    children: [
                      {
                        title: '企业信息',
                        path: ':id/approval',
                        element: <EnterpriseApproval />,
                      },
                      {
                        title: '企业信息',
                        path: ':id/edit',
                        element: <EnterpriseEdit />,
                      },
                    ],
                  },
                  {
                    title: '应用信息管理',
                    path: 'appList',
                    element: <AppList />,
                    children: [
                      {
                        title: '应用信息管理',
                        path: 'newdata',
                        element: <AppNewData />,
                      },
                      {
                        title: '编辑应用信息',
                        path: ':id/edit',
                        element: <AppEdit />,
                      },
                    ],
                  },
                  {
                    title: '应用信息查询',
                    path: 'app-search',
                    element: <AppSearch />,
                  },
                  {
                    title: '在线用户',
                    path: 'online-user',
                    element: <OnlineUser />,
                  },
                  {
                    title: '用户列表',
                    path: 'user-list',
                    element: <UserList />,
                  },
                  {
                    title: '对接者列表',
                    path: 'dev-user-list',
                    element: <DevUserList />,
                  },
                  {
                    title: '意见反馈管理',
                    path: 'feedback-management',
                    element: <FeedbackManagement />,
                  },
                  {
                    title: 'APP版本管理',
                    path: 'app-ver-mng',
                    element: <PlatSysAppVer />,
                  },
                  {
                    title: '用户登录日志',
                    path: 'plat-user-login-log',
                    element: <PlatSysUserLog />,
                  },
                  {
                    title: '用户操作日志',
                    path: 'user-action-log',
                    element: <UserAction />,
                  },
                  {
                    title: '应用日活统计',
                    path: 'app-daily-activeness',
                    element: <AppDaily />,
                  },
                  {
                    title: '应用访问日志',
                    path: 'app-access-log',
                    element: <PlatSysAppLog />,
                  },
                  {
                    title: '异常账号日志',
                    path: 'excpt-account-log',
                    element: <PlatSysExceptionLog />,
                  },
                  {
                    title: '用户信息',
                    path: 'developer-info',
                    element: <PlatDevUserInfo />,
                  },
                  {
                    title: '在线用户',
                    path: 'online-developer',
                    element: <PlatDevOnlineUser />,
                  },
                  {
                    title: '操作记录',
                    path: 'developer-login-log',
                    element: <PlatDevLoginLog />,
                  },
                  {
                    title: '月活跃度',
                    path: 'monthly-activeness',
                    element: <PlatDevMonthlyActiveness />,
                  },
                  {
                    title: '项目管理',
                    path: '/proj-mng',
                    element: <PlatDevProj />,
                  },
                  {
                    title: '用例管理',
                    path: 'use-case-mngmnt',
                    element: <PlatDevUseCase />,
                  },
                  {
                    title: '企业管理',
                    path: 'enterprise-mngmnt',
                    element: <PlatDevEtp />,
                  },

                  // 以上为平台控制台路由
                  // 以下为企业控制台路由

                  {
                    title: '组织机构管理',
                    path: 'system-overview',
                    element: <SystemOverview />,
                  },
                  {
                    title: '组织机构管理',
                    path: 'organization',
                    element: <CorpEtpOrganization />,
                  },
                  {
                    title: '平台认证管理',
                    path: 'platform-authentication',
                    element: <CorpEtpPlatformAuth />,
                  },
                  {
                    title: '二级节点认证管理',
                    path: 'second-level-nodes-authentication',
                    element: <CorpEtpSecNodes />,
                  },
                  {
                    title: '认证结果',
                    path: 'auth-result',
                    element: <CorpEtpAuthResult />,
                  },

                  {
                    title: '应用管理',
                    path: 'application',
                    element: <CorpAppApp />,
                  },
                  {
                    title: '权限管理',
                    path: 'authorization',
                    element: <CorpAppPermission />,
                  },

                  {
                    title: '人员同步',
                    path: 'member-sync',
                    element: <CorpDevMember />,
                  },
                  {
                    title: '对接者管理',
                    path: 'communicator',
                    element: <CorpDevCommunicator />,
                  },
                  {
                    title: '对接权限管理',
                    path: 'communication-authorization',
                    element: <CorpDevCommunicationAuth />,
                  },
                  {
                    title: '第三方对接配置',
                    path: 'third-party',
                    element: <CorpDevThirdParty />,
                  },
                  {
                    title: '公告发布管理',
                    path: 'announcement',
                    element: <CorpSysAnnouncement />,
                  },
                  {
                    title: '待办管理',
                    path: 'todo',
                    element: <CorpSysTodo />,
                  },
                  {
                    title: '用户登录日志',
                    path: 'corp-user-login-log',
                    element: <CorpSysUserLog />,
                  },
                  {
                    title: '应用访问日志',
                    path: 'application-access-log',
                    element: <CorpSysAppLog />,
                  },
                  {
                    title: 'banner配置',
                    path: 'banner',
                    element: <Banner />,
                  },
                  {
                    title: '数据卡片配置',
                    path: 'dataCard',
                    element: <DataCard />,
                  },
                  {
                    title:'新闻中心',
                    path:'news',
                    element:<NewsList/>
                  },

                  { title: '页面不存在', path: '*', element: <NotFound /> },
                ],
              },
            ],
          },
          {
            element: <BasicLayout showMiddleMenu={true} />, // 基础的布局不需要异步，避免页面抖动
            children: [
              {
                element: <ColumnLayout />,
                children: [
                  {
                    title: '首页',
                    path: 'common-index',
                    element: <CommonIndex />,
                  },
                  {
                    title: '应用',
                    path: 'common-app/*',
                    element: <CommonApp />,
                  },
                  {
                    title: '待办',
                    path: 'common-todos',
                    element: <CommonTodos />,
                  },
                  {
                    title: '消息',
                    path: 'common-msg',
                    element: <CommonMsg />,
                  },
                  { title: '页面不存在', path: '*', element: <NotFound /> },
                ],
              },
            ],
          },
          // 企业通过平台认证之前只能访问两个页面
          {
            element: <BasicLayout showMiddleMenu={false} />,
            children: [
              {
                element: <ColumnLayout />,
                children: [
                  {
                    title: '平台认证管理',
                    path: 'before-authorization-form',
                    element: <CorpEtpPlatformAuth />,
                  },
                  {
                    title: '认证结果',
                    path: 'before-authorization-auth-result',
                    element: <CorpEtpAuthResult />,
                  },
                ],
              },
            ],
          },
          { title: '404-页面未找到', path: '*', element: <NotFound /> },
        ],
      },
      // 登录前
      {
        title: '',
        path: '/',
        element: <GuestLayout />,
        children: [
          { title: '登录', path: 'login', element: <Login /> },
          { title: '注册成功', path: 'signup/success', element: <SignupSuccess /> },
          {
            title: '重置密码',
            path: 'password-forgot/:companyId/:phone/:code',
            element: <ForgotStep2 />,
          },
          { title: '重置密码', path: 'password-forgot', element: <Forgot /> },
          {
            title: '密码重置成功',
            path: 'password-forgot/success',
            element: <PasswordForgotSuccess />,
          },
          { title: '注册', path: 'signup', element: <Signup /> },
        ],
      },
      { title: '404-页面未找到', path: '*', element: <NotFound /> },
    ],
    []
  );
  const elements = useRoutes(routes as RouteObject[]);
  return (
    <RouteContext.Provider value={{ routes }}>
      <ErrorBoundary>
        <React.Suspense fallback={<Spin />}>{elements}</React.Suspense>
      </ErrorBoundary>
    </RouteContext.Provider>
  );
};

export default App;
